/**
 * Event emitter interface.
 */
export interface IEventEmitter {
  on: (event: string, cb: ( ...args: any[]) => void) => void,
  once: (event: string, cb: ( ...args: any[]) => void) => void,
  off: (event: any,  cb?: ( ...args: any[]) => void) => number,
  removeAllListeners: (event?: any) => number,
  emit: (event: string, ...args: any[]) => void
}

/**
 * Simple event emitter implementation.
 */
export class EventEmitter implements IEventEmitter {
  readonly eventListeners : {[key: string]: Function[] } = {};

  constructor() { }
  public on(event: string, cb: (...args: any[]) => void) {
    if(!(event in this.eventListeners)) {
        this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(cb);
  }

  public once(event: string, cb: (...args: any[]) => void) {
    const onceCb = (...args: any[]) => {
        cb(...args);
        this.off(event, onceCb)
    }
    this.on(event, onceCb);
  }

  public off(event: string, cb?: (...args: any[]) => void) {
    if(!cb) {
        return this.removeAllListeners(event);
    }
    if(!this.eventListeners[event]) {
        return 0;
    }
 
    const index = this.eventListeners[event].indexOf(cb);
    if(index > -1) {
        this.eventListeners[event].splice(index, 1);
        if(!this.eventListeners[event].length) {
            delete this.eventListeners[event];
        }
        return 1;
    }
    return 0;
  }

  /**
   * Will remove all listeners for event name if provided, otherwise if not provided will remove all event listeners of all events
   * @param event 
   * @returns number of events removed
   */
  public removeAllListeners(event?: string) {
    if(event) {
        const length = this.eventListeners[event]?.length || 0;
        delete this.eventListeners[event];
        return length;
    } else {
        let totalEvents = 0;
        const keys = Object.keys(this.eventListeners);
        for(let i = 0; i < keys.length; i++) {
            totalEvents += this.removeAllListeners(keys[i]);
        }
        return totalEvents;
    }
  }

  public emit(event: string, ...args: any[]) {
    // Iterate a SNAPSHOT: a listener may remove itself (or others) mid-dispatch
    // — notably `once`, which splices the live array as it fires. Without the
    // copy, a self-removing listener shifts the array and the forEach skips the
    // next listener. Matches Node's EventEmitter (also snapshots), so listeners
    // added during an emit do not fire until the next one.
    this.eventListeners[event]?.slice().forEach(cb => cb(...args));
  }
}